import { DOCUMENT, inject, Injectable, DestroyRef } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
  AvailableLanguageCodesEnum,
  MultiLanguageString,
  Translations
} from '@ui/shared/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import moment from 'moment';
// You have to import the locales in order to be able to switch to them.
import 'moment/locale/de.js';
import 'moment/locale/en-gb.js';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { LocalStorageService } from '../storage/local-storage.service';
import { INFRASTRUCTURE_CONFIG } from '../infrastructure-config.token';
import { getDefaultLanguageCode } from '../base-state/app/app.selectors';
import { languages } from './languages';

const TRANSLATIONS_STORAGE_KEY = 'translations';

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  #destroyRef = inject(DestroyRef);
  #http = inject(HttpClient);
  #document = inject(DOCUMENT);

  private store = inject(Store);
  private translate = inject(TranslateService);
  private storage = inject(LocalStorageService);
  private infrastructureConfig = inject(INFRASTRUCTURE_CONFIG);

  public defaultLanguageCode: AvailableLanguageCodesEnum;

  constructor() {
    this.store
      .select(getDefaultLanguageCode)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(defaultLanguageCode => {
        this.defaultLanguageCode = defaultLanguageCode;
      });
  }

  public getTranslations() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.storage.getItem(TRANSLATIONS_STORAGE_KEY);
  }

  public translationExists(localeKey: string, informal: boolean) {
    const fromStorage = this.getTranslations();
    const key = informal ? `${localeKey}_informal` : localeKey;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return fromStorage && fromStorage[key];
  }

  public setTranslations(
    localeKey: string,
    informal: boolean,
    translations: Translations
  ) {
    const key = informal ? `${localeKey}_informal` : localeKey;
    this.translate.setTranslation(key, translations[localeKey], false);
    this.translate.setFallbackLang(key);
    moment.locale(localeKey);

    this.translate.use(key);

    // keep <html lang> in sync with the active locale so assistive tech
    // announces content in the right language (WCAG 3.1.1 / 3.1.2)
    this.#document.documentElement.lang = localeKey;

    this.setLanguageFromDesignLib(localeKey, key);

    this.saveTranslations(translations);
  }

  public saveTranslations(translations: Translations) {
    const value = {
      ...(this.storage.getItem(TRANSLATIONS_STORAGE_KEY) || {}),
      ...translations
    };

    this.storage.setItem(TRANSLATIONS_STORAGE_KEY, value);
  }

  public removeTranslations() {
    this.storage.removeItem(TRANSLATIONS_STORAGE_KEY);
  }

  public getExtendedLocale(locale: string) {
    return languages[locale] && languages[locale].extendedLocale;
  }

  public getInfrastructureLanguageConfig() {
    const { defaultLanguageCode, availableLanguagesByCode } =
      this.infrastructureConfig.languages;

    return {
      defaultLanguageCode,
      availableLanguages: availableLanguagesByCode.map(code => languages[code])
    };
  }

  public getMultiLanguageStringValue(
    multiLangStr: MultiLanguageString | undefined,
    langCode = this.defaultLanguageCode
  ): string {
    const languageIsDefault = langCode === this.defaultLanguageCode;

    let value = multiLangStr?.[langCode];

    // use value in default app language as fallback:
    if (!value && !languageIsDefault) {
      value = multiLangStr?.[this.defaultLanguageCode];
    }

    return value;
  }

  /**
   * Loads design library translations for the given locale and merges them
   * into the active translation set under the provided key.
   *
   * Fetches `assets/im-lib-i18n/{localeKey}.json` once and registers the
   * translations via `TranslateService.setTranslation` with the merge flag
   * set to `true`, so existing translations under `key` are preserved.
   *
   * @param localeKey - Locale identifier used to resolve the JSON file
   *                    (e.g. `de`, `en`).
   * @param key - Translation key under which the design library translations
   *              are merged (typically `localeKey` or `${localeKey}_informal`).
   */
  private setLanguageFromDesignLib(localeKey: string, key: string) {
    this.#http
      .get<{ [key: string]: string }>(`assets/im-lib-i18n/${localeKey}.json`)
      .pipe(take(1))
      .subscribe(translations =>
        this.translate.setTranslation(key, translations, true)
      );
  }
}
